"use client";

import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import type { AuthEntryPoint } from "@/lib/analytics/types";
import { track } from "@/lib/analytics/tracker";
import type { SignedIn } from "@/lib/auth/sign-in/result";
import { signInWithPassword } from "../sign-in-actions";
import { SocialSignIn } from "../social-sign-in";
import { AuthField } from "./auth-field";
import { AuthError, AuthNotice } from "./auth-messages";
import { ForgotPasswordButton, useForgotPassword } from "./forgot-password";
import { useAuthCompletion } from "./use-auth-completion";

export interface LogInStepProps {
  entryPoint: AuthEntryPoint;
  initialEmail?: string;
  quoteClaimToken?: string;
  notice?: string;
  showProviders: boolean;
  onSignedIn: (result: SignedIn) => void | Promise<void>;
  onNeedsCode: () => void;
  onUseCode: (email: string) => void;
  onSignUp: (email: string) => void;
}

export function LogInStep(props: LogInStepProps) {
  const [email, setEmail] = useState(props.initialEmail ?? "");
  const [password, setPassword] = useState("");
  const forgot = useForgotPassword();
  const completion = useAuthCompletion({
    entryPoint: props.entryPoint,
    surface: "password",
    method: "password",
    onSignedIn: props.onSignedIn,
    onNeedsCode: props.onNeedsCode,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    track("auth.started", { entry_point: props.entryPoint, method: "password" });
    void completion.run(() => signInWithPassword({ email, password, quoteClaimToken: props.quoteClaimToken }));
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <AuthNotice message={props.notice} />
      <AuthField label="Email" type="email" autoComplete="email" autoFocus value={email} onChange={setEmail} />
      <AuthField
        label="Password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
        labelAction={<ForgotPasswordButton isSending={forgot.isSending} onClick={() => void forgot.request(email)} />}
      />
      <AuthNotice message={forgot.message ?? undefined} />
      <AuthError message={completion.error} />
      <Button type="submit" variant="primary" className="w-full" disabled={completion.isBusy}>
        {completion.isBusy ? "Logging in…" : "Log in"}
      </Button>
      <Button type="button" variant="outline" className="w-full" disabled={completion.isBusy} onClick={() => props.onUseCode(email)}>
        Email me a code instead
      </Button>
      {props.showProviders ? <SocialSignIn /> : null}
      <p className="text-center text-sm text-muted-foreground">
        New to Repackify?{" "}
        <button type="button" className="font-medium text-foreground underline disabled:opacity-50" disabled={completion.isBusy} onClick={() => props.onSignUp(email)}>
          Create an account
        </button>
      </p>
    </form>
  );
}
